import { useEffect, useState } from "react"
import usePlacesAutocomplete, { getGeocode, getLatLng } from "use-places-autocomplete"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@components/common/command"
import { Popover, PopoverContent, PopoverTrigger } from "@components/common/popover"
import { ChevronsUpDown } from "lucide-react"
import { cn } from "@pma/utils/helpers"
import useTranslator from "@hooks/use-translator"

interface Props {
  setGeoLocation: (location: { name: string; long: number; lat: number }) => void
  location: string
  disabled?: boolean
}

const GeoCoder = ({ setGeoLocation, location, disabled }: Props) => {
  const { translate, locale } = useTranslator()

  const isHebrew = locale === "he"
  const [isOpen, setIsOpen] = useState(false)

  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearCache,
    clearSuggestions,
    init,
  } = usePlacesAutocomplete({
    requestOptions: {
      language: isHebrew ? "he" : "en",
      // componentRestrictions: { country: 'il' }, // Uncomment to restrict to Israel
    },
    debounce: 300,
  })

  // Reinitialize when locale changes to update the language setting
  useEffect(() => {
    if (ready) {
      init()
    }
  }, [locale])

  const handleSelect = async (description: string) => {
    setValue(description, false)
    clearSuggestions()

    try {
      const results = await getGeocode({ 
        address: description,
        language: isHebrew ? "he" : "en", // Also set language for geocoding
      })
      const { lat, lng } = await getLatLng(results[0])

      setGeoLocation({
        name: description,
        long: lng,
        lat: lat,
      })
      setIsOpen(false)
      clearCache()
    } catch (error) {
      console.error("Error getting geocode:", error)
    }
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger
        disabled={disabled || !ready}
        className={cn(
          "flex w-full items-center justify-between rounded-md border p-1.5 text-sm",
          (disabled || !ready) && "cursor-not-allowed text-gray-500",
        )}
      >
        <div className={cn("w-full", isHebrew ? "text-right" : "text-left")}>
          {location ? (
            <div className='w-[23ch] overflow-hidden truncate text-ellipsis p-0.5 text-sm leading-4'>
              {location}
            </div>
          ) : (
            translate("Search Address")
          )}
        </div>
        <ChevronsUpDown size={12} />
      </PopoverTrigger>

      <PopoverContent className='p-0' dir={isHebrew ? "rtl" : "ltr"}>
        <Command dir={isHebrew ? "rtl" : "ltr"}>
          <CommandInput
            placeholder={translate("Search Address")}
            value={value}
            onValueChange={(val) => setValue(val)}
            dir={isHebrew ? "rtl" : "ltr"}
          />
          <CommandList>
            <CommandEmpty>{translate("No locations match")}</CommandEmpty>
            <CommandGroup>
              {status === "OK" &&
                data.map(({ place_id, description }) => (
                  <CommandItem
                    key={place_id}
                    value={description}
                    onSelect={() => handleSelect(description)}
                    className={cn("my-1", isHebrew && "text-right")}
                  >
                    {description}
                  </CommandItem>
                ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default GeoCoder